import React from 'react';

import type { OCRData } from './types';

type Props = {
  ocrData: OCRData | null;
  currentPage: number;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  originalWidth: number;
  originalHeight: number;
  setOcrData: React.Dispatch<React.SetStateAction<OCRData | null>>;
  canvasRect: { width: number; height: number } | null;
};

/**
 * OCRTextOverlay
 *
 * Renders an invisible-but-selectable text layer on top of the PDF canvas.
 * The geometry is computed in the *unzoomed* layout space; the page wrapper
 * is scaled via CSS, so this overlay stays in lock-step with the PDF & BBoxes.
 */
const OCRTextOverlay: React.FC<Props> = ({
  ocrData,
  currentPage,
  originalWidth,
  originalHeight,
  canvasRect,
}) => {
  const pageKey = `Page_${currentPage}`;
  const ocrPage = ocrData?.[pageKey];

  if (!ocrPage || !canvasRect) return null;

  const baseWidth = ocrPage.width || originalWidth || 0;
  const baseHeight = ocrPage.height || originalHeight || 0;

  if (!baseWidth || !baseHeight) return null;

  const scaleX = canvasRect.width / baseWidth;
  const scaleY = canvasRect.height / baseHeight;

  return (
    <>
      {ocrPage.OCR.map((item, idx) => {
        const [x1, y1, x2, y2] = item.bbox;

        const left = x1 * scaleX;
        const top = y1 * scaleY;
        const width = (x2 - x1) * scaleX;
        const height = (y2 - y1) * scaleY;

        return (
          <span
            key={idx}
            style={{
              position: 'absolute',
              left,
              top,
              width,
              height,
              color: 'transparent',
              userSelect: 'text',
              pointerEvents: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: height,
              lineHeight: `${height}px`,
              overflow: 'hidden',
            }}
          >
            {item.text}
          </span>
        );
      })}
    </>
  );
};

export default OCRTextOverlay;
